<!-- Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. -->

<form
	id="ticketEditForm"
	*ngIf="{ activeLineDate: (activeLineDate$ | async), isGTPEnabled: (isGTPEnabled$ | async) } as state"
	[formGroup]="ticketForm"
	class="ticket-edit-layout {{ this.classOverrides ? this.classOverrides : this.ticketType }}"
	[ngClass]="{
		'gtp-enabled': state.isGTPEnabled,
		'plan-granularity-hour':
			state.isGTPEnabled && this.ticketForm.get('livePlanGranularity')?.value === 'hour'
	}"
>
	<mat-form-field class="activity-name" *ngIf="ticketForm.get('activityName') as activityNameControl">
		<mat-label>{{ appStrings.TICKET_EDIT_DESCRIPTION }}</mat-label>
		<textarea
			matInput
			type="text"
			formControlName="activityName"
			automationId="ticketEditDescriptionInput"
			autocomplete=""
		></textarea>
	</mat-form-field>

	<mat-form-field class="role" *ngIf="ticketForm.get('liveRole') as roleControl">
		<mat-label>{{ appStrings.PROJECT_ITEM_ROLE_SINGULAR }}</mat-label>
		<mat-select
			formControlName="liveRole"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="projectItemLiveRole"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div class="color-swath" [ngStyle]="{ 'background-color': roleControl.value?.view?.color }"></div>
					<div>{{ roleControl.value?.view?.roleName }}</div>
				</div>
			</mat-select-trigger>
			<mat-option *ngFor="let role of roleList$ | async" [value]="role">
				<div class="options-choices">
					<div class="color-swath" [ngStyle]="{ 'background-color': role?.view?.color }"></div>
					<div>{{ role?.view?.roleName }}</div>
				</div>
			</mat-option>
		</mat-select>
	</mat-form-field>

	<mat-form-field class="location" *ngIf="ticketForm.get('location') as locationControl">
		<mat-label>{{ appStrings.PROJECT_ITEM_LOCATION_SINGULAR }}</mat-label>

		<mat-select
			formControlName="location"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="projectItemLocation"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': locationControl.value?.view?.color }"
					></div>
					<div>{{ locationControl.value?.view?.name }}</div>
				</div>
			</mat-select-trigger>
			<!-- BEGIN PLACEHOLDER REPLACE AS PART OF TP-7288 -->
			<mat-option [value]="null">{{ appStrings.GENERAL_NONE }}</mat-option>
			<mat-option *ngFor="let location of locationList$ | async" [value]="location">
				<div class="options-choices">
					<div class="color-swath" [ngStyle]="{ 'background-color': location.view.color }"></div>
					<div>{{ location.view.name }}</div>
				</div>
			</mat-option>
			<!-- END PLACEHOLDER REPLACE AS PART OF TP-7288 -->
		</mat-select>
	</mat-form-field>

	<mat-radio-group
		*ngIf="ticketForm.get('livePlanGranularity')"
		class="plan-granularity"
		formControlName="livePlanGranularity"
	>
		<mat-radio-button value="day" automationId="planByDayRadio">{{
			appStrings.PLAN_BY_DATE
		}}</mat-radio-button>
		<mat-radio-button value="hour" automationId="planByHourRadio">{{
			appStrings.PLAN_BY_TIME
		}}</mat-radio-button>
	</mat-radio-group>

	<mat-form-field class="milestone-type" *ngIf="ticketForm.get('milestoneType')">
		<mat-label>{{ appStrings.TICKET_MILESTONE_TYPE_PLACEHOLDER }}</mat-label>
		<mat-select formControlName="milestoneType" automationId="milestoneTypeSelector">
			<mat-option>{{ appStrings.UICONTROL_TEXT_NOT_SET }}</mat-option>
			<mat-option *ngFor="let milestoneType of milestoneTypes" [value]="milestoneType.value">
				{{ milestoneType.label }}
			</mat-option>
		</mat-select>
	</mat-form-field>

	<mat-form-field
		class="external-schedule"
		*ngIf="ticketForm.get('externalScheduleId') as externalScheduleIdControl"
	>
		<mat-label>{{ appStrings.TICKET_EDIT_MILESTONE_EXTERNAL_SCHEDULE_ID }}</mat-label>
		<input matInput type="text" formControlName="externalScheduleId" />
	</mat-form-field>

	<!-- This form control is currently set to readonly. If you decide to change this, ensure that you relocate the logic for hiding/showing elsewhere, -->
	<!-- away from the UI. Avoid using ngIf for this purpose, as it may result in the form attempting to "save" with a nullish value. -->
	<!-- A preferable approach is either to exclude the form control or to remove it based on the specified logic while creating the formGroup. -->
	<!-- Take a look to this.formManager.setOverrideTypeConfig(configOverrides); on src/app/modules/reports/services/reports.service.ts for an example. -->
	<mat-form-field
		class="external-schedule"
		*ngIf="(isTicketWbsEnabled$ | async) && (isAdmin$ | async) && ticketForm.get('wbsCode')"
	>
		<mat-label>{{ appStrings.WBS_CODE }}</mat-label>
		<input matInput type="text" formControlName="wbsCode" automationId="wbsCode" />
	</mat-form-field>

	<!--BEGIN PLACEHOLDER REPLACE AS PART OF TP-7104 -->
	<!-- We've changed the core assignment behavior here, ux design is required
	 assume that everything in here is suspect -->
	<mat-form-field
		class="assigned-to"
		[ngClass]="{
			hidden:
				!ticketForm?.get('assignedProjectMember')?.value && ticketForm.get('responsibleProjectMember')?.value
		}"
		*ngIf="ticketForm.get('assignedProjectMember') as assignedProjectMemberControl"
	>
		<mat-label>{{ appStrings.CONSTRAINT_ASSIGNED_TO }}</mat-label>
		<mat-select
			formControlName="assignedProjectMember"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="assignedProjectMember"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': assignedProjectMemberControl.value?.view?.role?.view?.color }"
					></div>
					<div>
						{{
							assignedProjectMemberControl.value?.view?.user?.firstName +
								' ' +
								assignedProjectMemberControl.value?.view?.user?.lastName ||
								assignedProjectMemberControl.value?.view.user.email
						}}
					</div>
				</div>
			</mat-select-trigger>
			<mat-option *ngFor="let projectMember of projectMembers$ | async" [value]="projectMember">
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': projectMember?.view?.role?.view?.color }"
					></div>
					<div>
						{{
							projectMember?.view?.user?.firstName + ' ' + projectMember?.view?.user?.lastName ||
								projectMember.view.user.email
						}}
					</div>
				</div>
			</mat-option>
		</mat-select>
	</mat-form-field>

	<!-- bunch o' horrible copy/ pasting that should be completely rewritten -->
	<ng-container *ngIf="!ticketForm.get('assignedProjectMember')?.value">
		<div class="assigned-to" *ngIf="responsibleProjectMember$ | async as responsibleProjectMember">
			<div class="options-choices">
				Accepted By:
				<div
					class="color-swath"
					[ngStyle]="{ 'background-color': responsibleProjectMember?.view?.role?.view?.color }"
				></div>
				<div>
					{{
						responsibleProjectMember?.view?.user?.firstName +
							' ' +
							responsibleProjectMember?.view?.user?.lastName || responsibleProjectMember?.view?.user?.email
					}}
				</div>
				<button
					mat-button
					class="remove-button"
					automationId="removeButton"
					(click)="ticketForm.get('assignedProjectMember')?.setValue(responsibleProjectMember)"
				>
					Re-assign Constraint
				</button>
			</div>
		</div>

		<!-- no idea why this version's not working, not really worth poking at it further for now -->
		<!-- <div class="assigned-to" *ngIf="(ticketForm.get('responsibleProjectMember')?.valueChanges | async) as responsibleProjectMemberControl">
			{{responsibleProjectMemberControl?.$id}}
			<div class="options-choices">
				<div
					class="color-swath"
					[ngStyle]="{ 'background-color': responsibleProjectMemberControl.value?.view?.role?.view?.color }"
				></div>
				<div>
					{{
						responsibleProjectMemberControl.value?.view?.user?.firstName +
							' ' +
							responsibleProjectMemberControl.value?.view?.user?.lastName ||
							responsibleProjectMemberControl.value?.view.user.email
					}}
				</div>
			</div>
		</div> -->
	</ng-container>

	<!-- temp disable - seems possible we may never want to enable responsible to ever be directly edited? -->
	<!-- <mat-form-field
		class="assigned-to"
		*ngIf="ticketForm.get('responsibleProjectMember') as responsibleProjectMemberControl"
	>
		<mat-label>{{ appStrings.CONSTRAINT_ASSIGNED_TO }}</mat-label>
		<mat-select
			formControlName="responsibleProjectMember"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="responsibleProjectMember"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': responsibleProjectMemberControl.value?.view?.role?.view?.color }"
					></div>
					<div>
						{{
							responsibleProjectMemberControl.value?.view?.user?.firstName +
								' ' +
								responsibleProjectMemberControl.value?.view?.user?.lastName ||
								responsibleProjectMemberControl.value?.view.user.email
						}}
					</div>
				</div>
			</mat-select-trigger>
			<mat-option *ngFor="let projectMember of projectMembers$ | async" [value]="projectMember">
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': projectMember?.view?.role?.view?.color }"
					></div>
					<div>
						{{
							projectMember?.view?.user?.firstName + ' ' + projectMember?.view?.user?.lastName ||
								projectMember.view.user.email
						}}
					</div>
				</div>
			</mat-option>
		</mat-select>
	</mat-form-field> -->

	<!-- END PLACEHOLDER REPLACE AS PART OF TP-7104 -->

	<mat-form-field class="priority" *ngIf="ticketForm.get('priority') as priorityControl">
		<mat-label>{{ appStrings.PRIORITY }}</mat-label>
		<mat-select
			formControlName="priority"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="priority"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div class="color-swath" [ngStyle]="{ 'background-color': priorityControl.value?.color }"></div>
					<div>{{ priorityControl.value?.text }}</div>
				</div>
			</mat-select-trigger>
			<mat-option *ngFor="let priority of priorityList$ | async" [value]="priority">
				<div class="options-choices">
					<div class="color-swath" [ngStyle]="{ 'background-color': priority.color }"></div>
					<div>{{ priority.text }}</div>
				</div>
			</mat-option>
		</mat-select>
	</mat-form-field>

	<mat-form-field class="date-requested" *ngIf="ticketForm.get('dateRequested') as dateRequestedControl">
		<mat-label>{{ appStrings.CONSTRAINT_DUE_DATE }}</mat-label>
		<input
			matInput
			[matDatepicker]="needbypicker"
			formControlName="dateRequested"
			automationId="constraintDueDatePicker"
		/>
		<mat-error *ngIf="dateRequestedControl.errors?.required">{{
			mobileStrings.ENTER_VALID_DATE_ERR
		}}</mat-error>
		<mat-datepicker-toggle automationId="needbypicker" matSuffix [for]="needbypicker"></mat-datepicker-toggle>
		<mat-datepicker touchUi #needbypicker></mat-datepicker>
	</mat-form-field>

	<!-- likely rendered pointless by the swtich to liveFinish. Can probably linger around just in case until after the edit dialog works -->
	<!-- <mat-form-field appearance="outline" class="actual-finish" *ngIf="this.ticketForm.get('actualFinish') as actualFinishControl">

		If this is uncommented, use a DateFormControlAdapter for the FormControl;
		see "liveStartAdapter" and "liveFinishAdapter" as examples.

		<mat-label>{{appStrings.PLANNED_DATE}}</mat-label>
		<input matInput [matDatepicker]="finishpicker" formControlName="actualFinish">
		<mat-datepicker-toggle matSuffix [for]="finishpicker"></mat-datepicker-toggle>
		<mat-datepicker touchUi #finishpicker ></mat-datepicker>
	</mat-form-field> -->
	<!-- <mat-form-field appearance="outline" class="actual-finish" *ngIf="this.ticketForm.get('plannedFinish') as plannedFinishControl">
		<mat-label>{{appStrings.PLANNED_DATE}}</mat-label>
		<input matInput [matDatepicker]="plannedpicker" formControlName="plannedFinish">
		<mat-datepicker-toggle matSuffix [for]="plannedpicker"></mat-datepicker-toggle>
		<mat-datepicker touchUi #plannedpicker ></mat-datepicker>
	</mat-form-field> -->

	<mat-form-field class="request-by" *ngIf="ticketForm.get('requestedByProjectMember') as requestedByControl">
		<mat-label>{{ appStrings.CONSTRAINT_REQUESTED_BY }}</mat-label>
		<mat-select
			formControlName="requestedByProjectMember"
			[compareWith]="defaultSplitDataSelectCompareFunc"
			automationId="requestedByProjectMember"
		>
			<mat-select-trigger>
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': requestedByControl.value?.view?.role?.view.color }"
					></div>
					<div>
						{{
							requestedByControl.value?.view?.user?.firstName +
								' ' +
								requestedByControl.value?.view?.user?.lastName || requestedByControl.value?.view?.user?.email
						}}
					</div>
				</div>
			</mat-select-trigger>
			<mat-option *ngFor="let projectMember of projectMembers$ | async" [value]="projectMember">
				<div class="options-choices">
					<div
						class="color-swath"
						[ngStyle]="{ 'background-color': projectMember?.view?.role?.view?.color }"
					></div>
					<div>
						{{
							projectMember?.view?.user?.firstName + ' ' + projectMember?.view?.user?.lastName ||
								projectMember?.view?.user?.email
						}}
					</div>
				</div>
			</mat-option>
		</mat-select>
	</mat-form-field>

	<mat-form-field class="start-date" *ngIf="ticketForm.get('liveStart') as liveStartControl">
		<mat-label>{{ startCalendarLabel(!!state.isGTPEnabled) }}</mat-label>
		<input
			matInput
			automationId="startDate"
			[max]="
				ticketForm.contains('liveFinish') && ticketForm.get('liveFinish')?.value < (state.activeLineDate || 0)
					? ticketForm.get('liveFinish')?.value
					: state.activeLineDate
			"
			[matDatepicker]="startpicker"
			formControlName="liveStart"
			readonly
			(click)="startpicker.open()"
		/>
		<mat-error *ngIf="liveStartControl.errors?.required">{{ mobileStrings.ENTER_VALID_DATE_ERR }}</mat-error>
		<mat-error *ngIf="ticketForm.errors?.invalidDateRange">{{
			mobileStrings.START_DATE_BIGGER_THEN_FINISH_DATE_ERR
		}}</mat-error>
		<mat-datepicker-toggle automationId="startpicker" matSuffix [for]="startpicker"></mat-datepicker-toggle>
		<mat-datepicker touchUi #startpicker></mat-datepicker>
	</mat-form-field>

	<mat-form-field class="finish-date" *ngIf="ticketForm.get('liveFinish') as liveFinishControl">
		<mat-label>{{ finishCalendarLabel(!!state.isGTPEnabled) }}</mat-label>
		<input
			matInput
			automationId="endDate"
			[min]="ticketForm.contains('liveStart') ? ticketForm.get('liveStart')?.value : ''"
			[max]="state.activeLineDate"
			[matDatepicker]="endpicker"
			formControlName="liveFinish"
			readonly
			(click)="endpicker.open()"
		/>
		<mat-error *ngIf="liveFinishControl.errors?.required">{{ mobileStrings.ENTER_VALID_DATE_ERR }}</mat-error>
		<mat-error *ngIf="liveFinishControl.errors?.matDatepickerMax">{{
			mobileStrings.FINISH_DATE_BIGGER_THEN_ACTIVE_LINE_DATE
		}}</mat-error>
		<mat-datepicker-toggle automationId="endpicker" matSuffix [for]="endpicker"></mat-datepicker-toggle>
		<mat-datepicker touchUi #endpicker></mat-datepicker>
	</mat-form-field>

	<mat-form-field *ngIf="ticketForm.get('liveStartTime')" class="start-time">
		<mat-label>{{ startTimeLabel() }}</mat-label>
		<input matInput formControlName="liveStartTime" automationId="startTime" />
		<mat-icon matSuffix>alarm</mat-icon>
	</mat-form-field>

	<mat-form-field *ngIf="ticketForm.get('liveFinishTime')" class="finish-time">
		<mat-label>{{ finishTimeLabel() }}</mat-label>
		<input matInput formControlName="liveFinishTime" automationId="finishTime" />
		<mat-icon matSuffix>alarm</mat-icon>
	</mat-form-field>

	<div *ngIf="ticketForm.get('liveDurationHours')" class="durationHours">
		<tp-duration-hours formControlName="liveDurationHours" #durationHoursComponent></tp-duration-hours>
		<mat-error
			*ngIf="
				(ticketForm.get('liveDurationHours')?.touched || durationHoursComponent.enableErrors) &&
				ticketForm.get('liveDurationHours')?.hasError('invalidDurationHours')
			"
		>
			{{ appStrings.DURATION_MIN }}
		</mat-error>
		<mat-error
			*ngIf="
				(ticketForm.get('liveDurationHours')?.touched || durationHoursComponent.enableErrors) &&
				ticketForm.get('liveDurationHours')?.hasError('invalidMaxDurationHours')
			"
		>
			{{ appStrings.DURATION_MAX }}
		</mat-error>
		<mat-error *ngIf="ticketForm.get('liveDurationHours')?.hasError('invalidHours')">
			{{ appStrings.INVALID_HOURS }}
		</mat-error>
		<mat-error *ngIf="ticketForm.get('liveDurationHours')?.hasError('invalidMinutes')">
			{{ appStrings.INVALID_MINUTES }}
		</mat-error>
	</div>

	<mat-form-field class="variance-reason" *ngIf="this.ticketForm.get('varianceReason')">
		<mat-label>{{ appStrings.VARIANCE_REASON }}</mat-label>
		<mat-select formControlName="varianceReason" automationId="varianceReason">
			<mat-option *ngFor="let variance of varianceList$ | async" [value]="variance.$id">
				{{ variance.varianceReason }}
			</mat-option>
		</mat-select>
	</mat-form-field>
	<mat-form-field class="variance-comment" *ngIf="this.ticketForm.get('varianceComment')">
		<mat-label>{{ 'Variance Comment' }}</mat-label>
		<textarea matInput formControlName="varianceComment"></textarea>
	</mat-form-field>

	<mat-number-spinner
		*ngIf="!state.isGTPEnabled && ticketForm.get('liveCrewSize') as liveCrewSizeControl"
		[label]="appStrings.CREW"
		class="crew-size"
		[min]="validationData['liveCrewSize'].min"
		[max]="validationData['liveCrewSize'].max"
		[floatingPoint]="true"
		formControlName="liveCrewSize"
		automationId="crewSize"
		[widthOfTextInChars]="3"
	>
	</mat-number-spinner>

	<mat-form-field
		class="crew-size"
		*ngIf="state.isGTPEnabled && ticketForm.get('liveCrewSize') as liveCrewSizeControl"
	>
		<mat-label>{{ appStrings.CREW }}</mat-label>
		<input
			matInput
			formControlName="liveCrewSize"
			type="number"
			step="1.0"
			automationId="crewSize"
			[min]="validationData['liveCrewSize'].min"
			[max]="validationData['liveCrewSize'].max"
		/>
	</mat-form-field>

	<mat-number-spinner
		*ngIf="!state.isGTPEnabled && ticketForm.get('liveDurationDays') as liveDurationDaysControl"
		[label]="appStrings.DAYS"
		class="duration-days"
		[min]="validationData['liveDurationDays'].min"
		[max]="validationData['liveDurationDays'].max"
		[floatingPoint]="true"
		[roundAfterPlusOrMinus]="true"
		automationId="durationDays"
		[widthOfTextInChars]="3"
		formControlName="liveDurationDays"
	>
	</mat-number-spinner>

	<mat-form-field class="duration-days" *ngIf="state.isGTPEnabled && ticketForm.get('liveDurationDays')">
		<mat-label>{{ appStrings.DAYS }}</mat-label>
		<input
			matInput
			formControlName="liveDurationDays"
			type="number"
			automationId="durationDays"
			step="1.0"
			[min]="validationData['liveDurationDays'].min"
			[max]="validationData['liveDurationDays'].max"
		/>
	</mat-form-field>

	<ng-container *ngIf="state.isGTPEnabled; else oldWorkDays">
		<div class="working-days" *ngIf="this.ticketForm.get('liveWorkingDays')">
			<tp-work-day-toggle
				class="working-days"
				automationId="workingDays"
				formControlName="liveWorkingDays"
				[liveStart$]="liveStartValue$"
				[calendarWorkdays]="calendarWorkdays"
				[editTicket$]="editTicket$"
			>
			</tp-work-day-toggle>

			<tp-work-schedule
				*ngIf="this.ticketForm.get('liveWorkSchedule')"
				formControlName="liveWorkSchedule"
				[liveStart$]="liveStartValue$"
				[calendarWorkdays]="calendarWorkdays"
				[editTicket$]="editTicket$"
				[workingDays]="ticketForm.get('liveWorkingDays')"
			></tp-work-schedule>
		</div>
	</ng-container>

	<ng-template #oldWorkDays>
		<workdays
			*ngIf="this.ticketForm.get('liveWorkingDays') as liveWorkingDaysControl"
			class="working-days"
			automationId="workingDays"
			formControlName="liveWorkingDays"
			[liveStart$]="liveStartValue$"
			[calendarWorkdays]="calendarWorkdays"
			[editTicket$]="editTicket$"
		>
		</workdays>
	</ng-template>

	<!-- BEGIN PLACEHOLDER REPLACE AS PART OF TP-4914 -->
	<mat-form-field class="master-schedule-link" *ngIf="this.ticketForm.get('masterScheduleLink')">
		<mat-label>Master Schedule Link (NYI)</mat-label>

		<input matInput type="text" formControlName="masterScheduleLink" automationId="masterScheduleLink" />
	</mat-form-field>
	<!-- END PLACEHOLDER REPLACE AS PART OF TP-4914 -->
</form>
